import React, {useContext, useEffect, useState} from 'react';
import Select, {SingleValueProps, components} from "react-select";
import {useProperties} from '../../contexts/properties/PropertiesContext';
import './Properties.scss';
import PropertyChangeE2eTestWrapper from "./e2e-utils/PropertyChangeE2eTestWrapper";
import {Button} from "@zendeskgarden/react-buttons";
import {ReactComponent as CircleFillIcon} from "@zendeskgarden/svg-icons/src/16/x-circle-fill.svg";
import {useListUserClientsQuery} from "../../__generated__/generated_types";
import "native-injects";
import {AuthContext} from "../../contexts/AuthContext";
import BusinessCenterIcon from '@material-ui/icons/Business';
import { ReactComponent as ChevronUpIcon } from '@zendeskgarden/svg-icons/src/16/chevron-down-stroke.svg';

type TOption = {value: string | undefined, label: string | undefined;};
const {DropdownIndicator, SingleValue} = components;

export const Properties: React.VFC = () => {
    const auth = useContext(AuthContext);
    const {
        properties,
        currentProperty,
        chooseProperty,
        loading,
        deactivateAcquisitionProperty
    } = useProperties();
    const [client, setClient] = useState<{clientId: string, clientName: string;}>();
    const [clientOptions, setClientOptions] = useState<TOption[]>([]);
    const {data: clients, loading: clientsLoading} = useListUserClientsQuery({
        fetchPolicy: "no-cache"
    });

    const mockLoading = [
        {
            value: 'noid',
            label: 'Loading...'
        }
    ];

    function singlePropertyValue(props: SingleValueProps<TOption, false>) {
        if(!client || (clients?.listUserClients?.length ?? 0) < 2) {
            return (
                <SingleValue {...props}>
                    {props.data.label}
                </SingleValue>
            );
        }
        return (
            <SingleValue {...props}>
                <div style={{lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis"}}>
                    {props.data.label}
                    <br />
                    <span style={{color: "var(--primary-400)"}}>{client.clientName}</span>
                </div>
            </SingleValue>
        );
    }

    useEffect(() => {
        if(loading || clientsLoading || !properties || !clients) {
            return;
        }
        // assuming all properties have same client
        const clientId = properties[0]?.clientId;
        if(!clientId) {
            return;
        }
        setClient(clients.listUserClients.find(c => c.clientId == clientId));
        setClientOptions(clients.listUserClients.map(uc => ({value: uc.clientId, label: uc.clientName})).sortBy("label"));
    }, [loading, clientsLoading, properties, clients]);

    return (
        <>
            {(loading || clientsLoading) &&
                <Select
                    className="fs-5 properties-select"
                    classNamePrefix="properties-select"
                    placeholder={"Select property"}
                    options={mockLoading}
                    defaultValue={mockLoading[0]}
                    value={mockLoading[0]}
                />
            }
            {!loading && !clientsLoading &&
                <>
                    {(!currentProperty || !currentProperty.isAcquisitionProperty ?
                        <>
                            <Select
                                className="fs-5 properties-select"
                                classNamePrefix="properties-select"
                                placeholder={"Select property"}
                                options={properties?.map(property => ({value: property.id, label: property.name}))}
                                onChange={property => {
                                    if(property && property.value) {
                                        chooseProperty(property.value);
                                    }
                                }}
                                components={{
                                    SingleValue: singlePropertyValue
                                }}
                                defaultValue={{value: currentProperty?.id, label: currentProperty?.name}}
                                value={{value: currentProperty?.id, label: currentProperty?.name}}
                                isDisabled={(properties?.length ?? 1) < 2}
                            />
                            <PropertyChangeE2eTestWrapper
                                properties={properties}
                                chooseProperty={chooseProperty}
                            />
                        </> :
                        <>
                            <div
                                    className="fs-5 properties-select properties-acquisition-selected-display"
                                    style={{display: "flex", alignItems: "center"}}
                            >
                                <div style={{flexGrow: 1, paddingLeft: "1rem"}}>
                                    {currentProperty?.name}
                                </div>
                                <div>
                                    <Button isBasic isDanger onClick={() => deactivateAcquisitionProperty()} aria-label="Deactivate Acquisition Property"><CircleFillIcon /></Button>
                                </div>
                            </div>
                        </>
                    )}
                    {clientOptions.length > 1 &&
                        <Select
                            className="properties-select-workspace-selector"
                            classNamePrefix="properties-select-workspace-selector"
                            options={clientOptions}
                            components={{
                                SingleValue: () => null,
                                IndicatorSeparator: () => null,
                                DropdownIndicator: (props) =>
                                    <DropdownIndicator {...props}>
                                        <div style={{display: "flex", alignItems: "center"}}>
                                            <BusinessCenterIcon style={{color: "var(--primary-400)", width: "32px"}} />
                                            <ChevronUpIcon style={{width: "16px", fontWeight: 500}}/>
                                        </div>
                                    </DropdownIndicator>,
                            }}
                            onChange={(val) => {
                                if(val?.value) {
                                    auth.switchUserClientId(val.value);
                                }
                            }}
                            value={{value: client?.clientId, label: client?.clientName}}
                        />
                    }
                </>
            }
        </>
    );
};
